// app/javascript/controllers/app_studio_side_navigation_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    connect() {
        // Restore saved sidebar state when page loads
        const isCollapsed = localStorage.getItem('sidebar-collapsed') === 'true'
        if (isCollapsed) {
            document.body.classList.add('sidebar-collapsed')
        }

        // Create tooltip element
        this.createTooltip()

        // Add event listeners for tooltips when collapsed
        this.setupTooltipListeners()
    }

    disconnect() {
        // Clean up tooltip
        if (this.tooltip) {
            this.tooltip.remove()
        }
    }

    toggleSidebar() {
        // Toggle the collapsed class on the body
        document.body.classList.toggle('sidebar-collapsed')

        // Save the state in localStorage so it persists across page loads
        const isCollapsed = document.body.classList.contains('sidebar-collapsed')
        localStorage.setItem('sidebar-collapsed', isCollapsed.toString())

        // Update tooltip listeners
        this.setupTooltipListeners()

        // Optional: Dispatch custom event for other components to listen to
        const event = new CustomEvent('sidebar:toggled', {
            detail: { collapsed: isCollapsed }
        })
        window.dispatchEvent(event)
    }

    toggleSubmenu(event) {
        const submenu = event.currentTarget.nextElementSibling
        if (submenu) {
            submenu.classList.toggle('show')
        }

        const icon = event.currentTarget.querySelector('.dropdown-btn-rotatable-icon')
        if (icon) {
            icon.classList.toggle('reverse-rotate-90')
        }
    }

    toggleAccordion(event) {
        const header = event.currentTarget
        const content = header.nextElementSibling
        const icon = header.querySelector('.accordion-icon')
        
        // Find all other accordions and close them
        const allHeaders = this.element.querySelectorAll('.section-title[data-action*="toggleAccordion"]')
        allHeaders.forEach(otherHeader => {
            if (otherHeader !== header) {
                const otherContent = otherHeader.nextElementSibling
                const otherIcon = otherHeader.querySelector('.accordion-icon')
                if (otherContent && otherContent.classList.contains('accordion-content')) {
                    otherContent.classList.add('hidden')
                    otherContent.classList.remove('block')
                }
                if (otherIcon) {
                    otherIcon.classList.remove('rotate-180')
                }
            }
        })
        
        // Toggle the clicked one
        if (content && content.classList.contains('accordion-content')) {
            if (content.classList.contains('hidden')) {
                content.classList.remove('hidden')
                content.classList.add('block')
                if (icon) icon.classList.add('rotate-180')
            } else {
                content.classList.add('hidden')
                content.classList.remove('block')
                if (icon) icon.classList.remove('rotate-180')
            }
        }
    }

    createTooltip() {
        // Create tooltip element if it doesn't exist
        if (!this.tooltip) {
            this.tooltip = document.createElement('div')
            this.tooltip.className = 'sidebar-tooltip'
            this.tooltip.style.cssText = `
        position: fixed;
        background: #1f2937;
        color: white;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        white-space: nowrap;
        z-index: 1000;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        pointer-events: none;
        opacity: 0;
        transform: translateY(-50%) translateX(-4px);
        transition: opacity 0.2s ease, transform 0.2s ease;
        display: none;
      `

            // Create text container
            this.tooltipText = document.createElement('span')
            this.tooltip.appendChild(this.tooltipText)

            // Add arrow
            const arrow = document.createElement('div')
            arrow.style.cssText = `
        position: absolute;
        left: -4px;
        top: 50%;
        transform: translateY(-50%);
        border: 4px solid transparent;
        border-right-color: #1f2937;
      `
            this.tooltip.appendChild(arrow)

            document.body.appendChild(this.tooltip)
        }
    }

    setupTooltipListeners() {
        const isCollapsed = document.body.classList.contains('sidebar-collapsed')
        const links = this.element.querySelectorAll('a[data-tooltip]')

        // Remove existing listeners
        links.forEach(link => {
            link.removeEventListener('mouseenter', this.boundShowTooltip)
            link.removeEventListener('mouseleave', this.boundHideTooltip)
        })

        // Add listeners only when collapsed
        if (isCollapsed && links.length > 0) {
            // Bind methods to preserve 'this' context
            this.boundShowTooltip = this.showTooltip.bind(this)
            this.boundHideTooltip = this.hideTooltip.bind(this)

            links.forEach(link => {
                link.addEventListener('mouseenter', this.boundShowTooltip)
                link.addEventListener('mouseleave', this.boundHideTooltip)
            })
        }
    }

    showTooltip(event) {
        const link = event.currentTarget
        const tooltipText = link.dataset.tooltip

        if (!tooltipText || !this.tooltip || !this.tooltipText) return

        // Get link position relative to viewport
        const rect = link.getBoundingClientRect()

        // Update tooltip content
        this.tooltipText.textContent = tooltipText

        // Position tooltip
        this.tooltip.style.left = `${rect.right + 8}px`
        this.tooltip.style.top = `${rect.top + rect.height / 2}px`
        this.tooltip.style.display = 'block'

        // Show tooltip with animation
        requestAnimationFrame(() => {
            this.tooltip.style.opacity = '1'
            this.tooltip.style.transform = 'translateY(-50%) translateX(0)'
        })
    }

    hideTooltip() {
        if (this.tooltip) {
            this.tooltip.style.opacity = '0'
            this.tooltip.style.transform = 'translateY(-50%) translateX(-4px)'

            // Hide after animation
            setTimeout(() => {
                this.tooltip.style.display = 'none'
            }, 200)
        }
    }
}